import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Flame, ShoppingCart, Zap } from 'lucide-react';
import { Button } from '../ui/button';
import { useCart } from '../../context/CartContext';
import { toast } from 'sonner';
import { getOptimizedImageUrl } from '../../utils/image';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Hot category handles to feature
const HOT_CATEGORIES = [
  { handle: 'bracelets', name: 'Healing Bracelets', emoji: '📿', color: 'from-purple-500 to-indigo-600' },
  { handle: 'crystal-trees', name: 'Crystal Trees', emoji: '🌳', color: 'from-emerald-500 to-teal-600' },
  { handle: 'gemstones', name: 'Gemstones', emoji: '💎', color: 'from-blue-500 to-cyan-600' },
  { handle: 'rudraksha', name: 'Rudraksha', emoji: '🪷', color: 'from-orange-500 to-red-600' },
];

export const HotProducts = () => {
  const [categoryProducts, setCategoryProducts] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(HOT_CATEGORIES[0].handle);
  const { addToCart, buyNow } = useCart();

  useEffect(() => {
    fetchHotProducts();
  }, []);

  const fetchHotProducts = async () => {
    try {
      const entries = await Promise.all(
        HOT_CATEGORIES.map((cat) =>
          fetch(`${API_URL}/api/products?category=${cat.handle}&limit=8&lite=true`)
            .then((r) => r.json())
            .then((d) => [cat.handle, Array.isArray(d) ? d : []])
            .catch(() => [cat.handle, []])
        )
      );
      setCategoryProducts(Object.fromEntries(entries));
    } catch (error) {
      console.error('Error fetching hot products:', error);
    }
    setLoading(false);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(price);
  };

  const handleAddToCart = (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product);
    toast.success(`${product.name} added to cart!`);
  };

  const handleBuyNow = (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    buyNow(product);
  };

  const currentProducts = categoryProducts[activeCategory] || [];
  const currentCategory = HOT_CATEGORIES.find(c => c.handle === activeCategory);

  if (loading) {
    return (
      <section className="py-12 lg:py-16 bg-gradient-to-b from-white to-amber-50/30">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-48 mb-4"></div>
            <div className="flex gap-4 mb-8">
              {[1,2,3,4].map(i => (
                <div key={i} className="h-12 w-32 bg-gray-200 rounded-full"></div>
              ))}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[1,2,3,4].map(i => (
                <div key={i} className="bg-white rounded-xl p-4">
                  <div className="aspect-square bg-gray-200 rounded-lg mb-3"></div>
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-12 lg:py-16 bg-gradient-to-b from-white to-amber-50/30" data-testid="hot-products-section">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-full mb-4">
            <Flame className="w-5 h-5 animate-pulse" />
            <span className="font-bold text-sm">HOT SELLING</span>
            <Flame className="w-5 h-5 animate-pulse" />
          </div>
          <h2 className="font-serif text-2xl lg:text-4xl font-bold text-foreground">
            Trending <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-orange-600">Products</span>
          </h2>
          <p className="text-muted-foreground mt-2">Most purchased items by our customers</p>
        </motion.div>

        {/* Category Pills */}
        <div className="flex flex-wrap justify-center gap-3 mb-8">
          {HOT_CATEGORIES.map((cat) => (
            <motion.button
              key={cat.handle}
              onClick={() => setActiveCategory(cat.handle)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`px-5 py-3 rounded-full font-medium text-sm transition-all flex items-center gap-2 ${
                activeCategory === cat.handle
                  ? `bg-gradient-to-r ${cat.color} text-white shadow-lg`
                  : 'bg-white border border-amber-200 text-foreground hover:border-amber-400'
              }`}
            >
              <span className="text-lg">{cat.emoji}</span>
              {cat.name}
            </motion.button>
          ))}
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
          {currentProducts.slice(0, 8).map((product, index) => {
            const discount = product.originalPrice > product.price
              ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
              : 0;

            return (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Link
                  to={`/product/${product.id}`}
                  className="group block bg-white rounded-2xl overflow-hidden border border-amber-100 hover:border-amber-300 hover:shadow-xl transition-all duration-300"
                >
                  {/* Image */}
                  <div className="relative aspect-square overflow-hidden bg-gradient-to-br from-amber-50 to-orange-50">
                    <img
                      src={getOptimizedImageUrl(product.image)}
                      alt={product.name}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    
                    {/* Badges */}
                    <div className="absolute top-2 left-2 flex flex-col gap-1">
                      {discount > 0 && (
                        <span className="px-2 py-1 rounded-md bg-green-500 text-white text-xs font-bold">
                          {discount}% OFF
                        </span>
                      )}
                      <span className="px-2 py-1 rounded-md bg-gradient-to-r from-red-500 to-orange-500 text-white text-xs font-bold flex items-center gap-1">
                        <Flame className="w-3 h-3" /> HOT
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    <h3 className="font-medium text-foreground text-sm line-clamp-2 mb-2 min-h-[2.5rem] group-hover:text-gold transition-colors">
                      {product.name}
                    </h3>

                    {/* Price */}
                    <div className="flex items-center gap-2 mb-3">
                      <span className="font-bold text-gold text-lg">
                        {formatPrice(product.price)}
                      </span>
                      {product.originalPrice > product.price && (
                        <span className="text-muted-foreground line-through text-sm">
                          {formatPrice(product.originalPrice)}
                        </span>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      <Button
                        onClick={(e) => handleAddToCart(e, product)}
                        size="sm"
                        variant="outline"
                        className="flex-1 border-amber-300 hover:bg-amber-50 text-xs"
                      >
                        <ShoppingCart className="w-4 h-4 mr-1" />
                        Add
                      </Button>
                      <Button
                        onClick={(e) => handleBuyNow(e, product)}
                        size="sm"
                        className="flex-1 gold-gradient text-white text-xs"
                      >
                        <Zap className="w-4 h-4 mr-1" />
                        Buy Now
                      </Button>
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>

        {/* View All */}
        <div className="text-center mt-8">
          <Link to={`/products?category=${activeCategory}`}>
            <Button 
              variant="outline" 
              className="border-gold text-gold hover:bg-gold hover:text-white px-8"
            >
              View All {currentCategory?.name}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};
